import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type { CalendarEvent, Task } from "@/planning";
import { EventModal } from "@/planning/client";
import { TaskModal } from "@/planning/client";
import { ArtworkImage } from "@/library/client";
import { InspirationModal } from "@/library/client";
import { MonthYearPicker } from "@/platform/ui";
import {
  IconCheck,
  IconChevronLeft,
  IconChevronRight,
  IconPlus,
  IconTriangleDown,
  IconTriangleUp,
} from "@/platform/ui";
import { CardTitle, CompactButton, CompactIconButton, SegmentedControl, cx } from "@/platform/ui";
import { useRecurrenceScope, type RecurrenceScope } from "@/platform/ui";
import { snippet } from "@/platform/client";
import { useActiveRegion } from "@/platform/client";
import { isTypingTarget } from "@/platform/client";
import { useCalendars, useEvents } from "@/planning/client";
import { useInspirationToday } from "@/library/client";
import { useTasks, useUpdateTask } from "@/planning/client";
import {
  WEEKDAYS,
  addDays,
  addMonths,
  dueTone,
  endOfMonth,
  fmtCivilDay,
  fmtDueLabel,
  fmtTime,
  homeToday,
  isoDateKey,
  monthGrid,
  sameDay,
  startOfMonth,
  startOfWeekMonday,
} from "@/platform/client";
import { buildDayEventMap } from "@/planning/client";
import { useTz } from "@/platform/client";

type View = "month" | "week";
type Scope = "day" | "week" | "month";

function eventTime(ev: CalendarEvent, tz: string): string {
  if (ev.allDay) return "All day";
  return fmtTime(new Date(ev.start), tz);
}

// This is a glance widget, not a triage tool — beyond this many open tasks,
// send the user to /tasks (mirrors Feeds' own "See all" pattern) rather than
// growing a scrollbar that would never actually need to scroll in practice.
const TASKS_SECTION_LIMIT = 4;

// Keyboard regions this sidebar participates in (see ../../lib/activeRegion.js)
// — claiming one of these on click is what stops the Feeds page's left
// column from keeping arrow-key ownership once something here is clicked.
const CALENDAR_REGION = "sidebar:calendar";
const AGENDA_REGION = "sidebar:agenda";
const TASKS_REGION = "sidebar:tasks";

// The day's curated quote + artwork. Deliberately the one thing in this
// sidebar that isn't tracking anything — it is here to be looked at, not acted
// on, so it has no counts, no controls, and no unread state.
//
// Art on top, quote below: the sidebar is the invitation, the modal is where
// the work actually breathes. Note that fetching this is what *assigns* the
// day's piece — days the app isn't opened cost nothing (docs/inspiration.md).
function InspirationSection() {
  const { data } = useInspirationToday();
  const [open, setOpen] = useState(false);
  const day = data?.day ?? null;

  return (
    <div className="flex flex-none flex-col p-4">
      <CardTitle
        actions={
          day && (
            <Link to="/art-gallery" className="text-[10.5px] font-bold text-accent hover:underline">
              Gallery
            </Link>
          )
        }
      >
        Art of the day
      </CardTitle>

      {!day ? (
        // Either nothing is imported yet, or the pool is spent. Both are the
        // same quiet non-event from here; the gallery's counter is where the
        // user finds out which.
        <div className="py-4 text-center text-xs font-semibold text-faint">
          {data?.poolEmpty ? "The well is dry" : "Nothing today"}
        </div>
      ) : (
        // The art and the quote, and nothing else — the same rule as the
        // modal. Attribution would put two name formats side by side, which
        // reads as one blob rather than two facts; the placard is one click
        // away, in the modal, where it has room.
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group flex flex-col gap-2.5 rounded-lg"
          aria-label="Open today's art"
        >
          <ArtworkImage
            artwork={day.piece.artwork}
            eager
            className="aspect-[4/3] w-full transition-opacity group-hover:opacity-90"
          />
          <blockquote className="text-center font-serif text-[13px] leading-relaxed text-ink-soft">
            “{snippet(day.piece.quote.text, 150)}”
          </blockquote>
        </button>
      )}

      <InspirationModal day={open ? day : null} onClose={() => setOpen(false)} />
    </div>
  );
}

export function RightSidebarOverview() {
  const { active: activeRegion, setActive: setActiveRegion } = useActiveRegion();
  const tz = useTz();
  const [view, setView] = useState<View>("month");
  const [scope, setScope] = useState<Scope>("day");
  // Today in the home zone until a day or a month is picked. While the setting
  // loads the browser's zone stands in, and around midnight it names another
  // day, so neither is fixed on the first render.
  const today = useMemo(() => homeToday(tz), [tz]);
  const [pickedMonth, setCursor] = useState<Date | null>(null);
  const [pickedDay, setSelected] = useState<Date | null>(null);
  const cursor = useMemo(() => pickedMonth ?? startOfMonth(today), [pickedMonth, today]);
  const selected = pickedDay ?? today;
  const [eventModal, setEventModal] = useState<{
    open: boolean;
    editing: CalendarEvent | null;
    scope: RecurrenceScope;
  }>({
    open: false,
    editing: null,
    scope: "all",
  });
  const askEventScope = useRecurrenceScope();
  const [taskModal, setTaskModal] = useState<{ open: boolean; editing: Task | null }>({
    open: false,
    editing: null,
  });

  const { data: calendars = [] } = useCalendars();
  const { data: openTasks = [] } = useTasks("open");
  const updateTask = useUpdateTask();

  // Overdue/Today/Upcoming, each sorted by due date ascending; undated tasks
  // land in Upcoming, last — a category is only ever shown once it has a task.
  const taskGroups = useMemo(() => {
    const byDue = (a: Task, b: Task) => {
      if (!a.dueAt && !b.dueAt) return 0;
      if (!a.dueAt) return 1;
      if (!b.dueAt) return -1;
      return a.dueAt.localeCompare(b.dueAt);
    };
    const groups = { overdue: [] as Task[], today: [] as Task[], upcoming: [] as Task[] };
    for (const t of openTasks) {
      const bucket = t.dueAt ? dueTone(t.dueAt, tz) : "upcoming";
      groups[bucket].push(t);
    }
    const ordered = [
      { key: "overdue", label: "Overdue", tasks: groups.overdue.sort(byDue) },
      { key: "today", label: "Today", tasks: groups.today.sort(byDue) },
      { key: "upcoming", label: "Upcoming", tasks: groups.upcoming.sort(byDue) },
    ];
    // Budget the display cap across groups in priority order, so a group that
    // fits entirely still gets its header and a later one can be cut off clean.
    let budget = TASKS_SECTION_LIMIT;
    return ordered
      .map((g) => {
        const tasks = g.tasks.slice(0, budget);
        budget -= tasks.length;
        return { ...g, tasks };
      })
      .filter((g) => g.tasks.length > 0);
  }, [openTasks, tz]);

  const grid = useMemo(() => monthGrid(cursor), [cursor]);
  const weekCells = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(startOfWeekMonday(selected), i)),
    [selected],
  );

  // agenda range — like the prototype: from the SELECTED day through the rest of
  // the week / month (not the whole week/month).
  const agenda = useMemo(() => {
    const base = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate());
    const day = (d: Date) => fmtCivilDay(isoDateKey(d), { year: false });
    const range = (end: Date) => ({
      from: base,
      to: addDays(end, 1),
      label: sameDay(end, base) ? day(base) : `${day(base)} – ${day(end)}`,
    });
    if (scope === "week") return range(addDays(startOfWeekMonday(base), 6)); // Sunday
    if (scope === "month") return range(endOfMonth(base));
    return range(base);
  }, [selected, scope]);

  const SCOPE_TABS: { value: Scope; label: string }[] = [
    { value: "day", label: "Selected day" },
    { value: "week", label: "Rest of week" },
    { value: "month", label: "Rest of month" },
  ];

  // fetch events covering both the mini-calendar grid and the agenda range
  const fetchRange = useMemo(() => {
    const from = grid[0]!.getTime() < agenda.from.getTime() ? grid[0]! : agenda.from;
    const gridEnd = addDays(grid[grid.length - 1]!, 1);
    const to = gridEnd.getTime() > agenda.to.getTime() ? gridEnd : agenda.to;
    return { from: from.toISOString(), to: to.toISOString() };
  }, [grid, agenda]);

  const { data: events = [] } = useEvents(fetchRange);

  const colorByCal = useMemo(() => {
    const m = new Map<number, string>();
    calendars.forEach((c) => m.set(c.id, c.color));
    return m;
  }, [calendars]);

  const eventsByDay = useMemo(() => buildDayEventMap(events, tz), [events, tz]);

  const agendaGroups = useMemo(() => {
    const out: { iso: string; date: Date; items: CalendarEvent[] }[] = [];
    for (const [iso, items] of eventsByDay) {
      // Parse the day key as a CIVIL date (local midnight) to match agenda.from/to,
      // rather than `new Date("YYYY-MM-DD")` which JS reads as UTC.
      const [y, m, dd] = iso.split("-").map(Number);
      const d = new Date(y!, m! - 1, dd!);
      if (d >= agenda.from && d < agenda.to) out.push({ iso, date: d, items });
    }
    return out.sort((a, b) => a.iso.localeCompare(b.iso));
  }, [eventsByDay, agenda]);

  function dotsFor(day: Date): string[] {
    const items = eventsByDay.get(isoDateKey(day)) ?? [];
    const colors: string[] = [];
    for (const ev of items) {
      const c = colorByCal.get(ev.calendarId) ?? "#9b87f5";
      if (!colors.includes(c)) colors.push(c);
      if (colors.length >= 3) break;
    }
    return colors;
  }

  function step(dir: number) {
    if (view === "week") {
      const d = addDays(selected, dir * 7);
      setSelected(d);
      setCursor(startOfMonth(d));
    } else {
      setCursor(addMonths(cursor, dir));
    }
  }
  function goToday() {
    const t = homeToday(tz);
    setCursor(startOfMonth(t));
    setSelected(t);
  }

  // Arrow-key day navigation — only fires while the mini-calendar holds the
  // active region (claimed by clicking a day, see `dayCell` below), so it
  // never competes with the Feeds page's left column or this sidebar's own
  // Feeds section for the same keystrokes.
  useEffect(() => {
    if (activeRegion !== CALENDAR_REGION) return;
    function onKeyDown(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) return;
      const dir =
        e.key === "ArrowLeft"
          ? -1
          : e.key === "ArrowRight"
            ? 1
            : e.key === "ArrowUp"
              ? -7
              : e.key === "ArrowDown"
                ? 7
                : null;
      if (dir == null) return;
      const next = addDays(selected, dir);
      setSelected(next);
      if (next.getMonth() !== cursor.getMonth() || next.getFullYear() !== cursor.getFullYear())
        setCursor(startOfMonth(next));
      e.preventDefault();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [activeRegion, selected, cursor]);

  const openNewEvent = () => setEventModal({ open: true, editing: null, scope: "all" });
  // Same up-front scope choice as the Calendar page — see docs/timezones.md.
  async function openEditEvent(ev: CalendarEvent) {
    const isSeries = ev.recurrenceRule != null || ev.masterEventId != null;
    const scope = isSeries
      ? await askEventScope({
          title: "Edit recurring event",
          message: "Edit just this event, or every event in the series?",
        })
      : "all";
    if (scope == null) return;
    setEventModal({ open: true, editing: ev, scope });
  }
  const openNewTask = () => setTaskModal({ open: true, editing: null });

  const dayCell = (day: Date, big = false) => {
    const inMonth = day.getMonth() === cursor.getMonth();
    const isToday = sameDay(day, homeToday(tz));
    const isSel = sameDay(day, selected);
    const dots = dotsFor(day);
    return (
      <button
        key={isoDateKey(day) + (big ? "w" : "m")}
        onClick={() => {
          setSelected(day);
          setActiveRegion(CALENDAR_REGION);
          if (day.getMonth() !== cursor.getMonth() || day.getFullYear() !== cursor.getFullYear()) {
            setCursor(startOfMonth(day));
          }
        }}
        onDoubleClick={() => {
          setSelected(day);
          openNewEvent();
        }}
        className={cx(
          "relative flex flex-col items-center justify-center gap-[3px] rounded-lg border transition-colors",
          big ? "py-2.5" : "min-h-[40px] py-1",
          isSel ? "border-accent bg-accent-soft" : "border-line hover:border-line-strong",
          !inMonth && view === "month" && "opacity-40",
        )}
      >
        <span
          className={cx(
            "flex items-center justify-center rounded-full text-[11.5px] font-bold",
            big ? "h-6 w-6" : "h-[21px] w-[21px]",
            isToday ? "bg-accent text-bg" : isSel ? "text-accent" : "text-ink-soft",
          )}
        >
          {day.getDate()}
        </span>
        <div className="flex h-1 gap-[2px]">
          {dots.map((c, i) => (
            <span key={i} className="h-1 w-1 rounded-full" style={{ background: c }} />
          ))}
        </div>
      </button>
    );
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto [scrollbar-gutter:stable]">
        {/* mini-calendar */}
        <div className="border-b border-line p-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <CompactIconButton label="Previous period" onClick={() => step(-1)}>
                <IconChevronLeft size={13} />
              </CompactIconButton>
              <MonthYearPicker
                cursor={cursor}
                short
                onSelect={(d) => {
                  setCursor(startOfMonth(d));
                  if (view === "week") setSelected(d);
                }}
              />
              <CompactIconButton label="Next period" onClick={() => step(1)}>
                <IconChevronRight size={13} />
              </CompactIconButton>
            </div>
            <div className="flex items-center gap-1.5">
              <CompactButton onClick={goToday} className="px-2 text-muted">
                Today
              </CompactButton>
              <SegmentedControl
                size="compact"
                value={view}
                onChange={setView}
                options={[
                  { value: "month", label: "Month" },
                  { value: "week", label: "Week" },
                ]}
              />
            </div>
          </div>

          <div className="mb-1.5 grid grid-cols-7 gap-[3px]">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                className="text-center text-[9.5px] font-extrabold tracking-wide text-faint"
              >
                {w}
              </div>
            ))}
          </div>
          {view === "month" ? (
            <div className="grid grid-cols-7 gap-[3px]">{grid.map((d) => dayCell(d))}</div>
          ) : (
            <div className="grid grid-cols-7 gap-[3px]">
              {weekCells.map((d) => dayCell(d, true))}
            </div>
          )}
        </div>

        {/* agenda */}
        <div className="flex-none border-b border-line p-4">
          <CardTitle
            actions={
              <CompactButton onClick={openNewEvent}>
                <IconPlus size={12} /> Event
              </CompactButton>
            }
          >
            Agenda
          </CardTitle>
          <div className="mono -mt-2 mb-2.5 text-[10.5px] font-bold text-faint">{agenda.label}</div>
          <SegmentedControl
            size="compact"
            full
            className="mb-2.5"
            value={scope}
            onChange={setScope}
            options={SCOPE_TABS}
          />
          <div className="max-h-[230px] space-y-2 overflow-y-auto">
            {agendaGroups.length === 0 ? (
              <div className="py-5 text-center text-xs font-semibold text-faint">
                Nothing scheduled
              </div>
            ) : (
              agendaGroups.map((g) => (
                <div key={g.iso}>
                  <div className="mb-1 flex items-center gap-2 px-0.5">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-surface-2 text-[11px] font-extrabold text-ink-soft">
                      {g.date.getDate()}
                    </span>
                    <span className="text-[10px] font-extrabold tracking-wide text-muted">
                      {WEEKDAYS[(g.date.getDay() + 6) % 7]}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    {g.items.map((ev) => (
                      <button
                        key={`${ev.id}:${ev.start}`}
                        onClick={() => {
                          setActiveRegion(AGENDA_REGION);
                          openEditEvent(ev);
                        }}
                        className="flex items-center gap-2.5 rounded-lg border border-line bg-surface px-2.5 py-2 text-left hover:border-line-strong"
                      >
                        <span
                          className="h-7 w-[3px] flex-none self-stretch rounded-sm"
                          style={{ background: colorByCal.get(ev.calendarId) ?? "#9b87f5" }}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-[12px] font-bold text-ink-soft">
                            {ev.title}
                          </div>
                          <div className="mono mt-0.5 text-[10px] font-semibold text-muted">
                            {eventTime(ev, tz)}
                          </div>
                          {snippet(ev.description) && (
                            <div className="mt-0.5 truncate text-[10px] text-faint">
                              {snippet(ev.description)}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* tasks */}
        <div className="flex flex-none flex-col p-4">
          <CardTitle
            actions={
              <CompactButton onClick={openNewTask}>
                <IconPlus size={12} /> Task
              </CompactButton>
            }
          >
            Tasks
          </CardTitle>
          <div className="flex flex-col">
            {openTasks.length === 0 ? (
              <div className="py-4 text-center text-xs font-semibold text-faint">No open tasks</div>
            ) : (
              taskGroups.map((g, gi) => (
                <div key={g.key} className={gi > 0 ? "mt-3" : undefined}>
                  <div className="mb-1.5 text-[10px] font-extrabold uppercase tracking-wider text-faint">
                    {g.label}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {g.tasks.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => setActiveRegion(TASKS_REGION)}
                        className="flex items-center gap-2.5 rounded-lg border border-line bg-surface px-2.5 py-2"
                      >
                        <button
                          onClick={() => updateTask.mutate({ id: t.id, patch: { status: "done" } })}
                          className="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[5px] border-[1.5px] border-line-strong hover:border-accent"
                          title="Complete"
                        >
                          <IconCheck size={11} className="opacity-0" />
                        </button>
                        <button
                          onClick={() => setTaskModal({ open: true, editing: t })}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="truncate text-[12.5px] font-semibold text-ink-soft">
                            {t.title}
                          </div>
                          {snippet(t.notes) && (
                            <div className="truncate text-[10px] text-faint">
                              {snippet(t.notes)}
                            </div>
                          )}
                        </button>
                        <div className="flex flex-none items-center gap-1.5">
                          {t.priority === "high" && (
                            <IconTriangleUp size={8} style={{ color: "var(--color-negative)" }} />
                          )}
                          {t.priority === "low" && (
                            <IconTriangleDown size={8} style={{ color: "var(--color-faint)" }} />
                          )}
                          {t.dueAt && (
                            <span
                              className={cx(
                                "mono text-[9.5px] font-bold",
                                dueTone(t.dueAt, tz) === "overdue" ? "text-negative" : "text-muted",
                              )}
                            >
                              {fmtDueLabel(t.dueAt, tz)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          {openTasks.length > TASKS_SECTION_LIMIT && (
            <Link
              to="/tasks"
              className="mt-2.5 self-start text-[10.5px] font-bold text-accent hover:underline"
            >
              See all
            </Link>
          )}
        </div>

        <div className="border-t border-line">
          <InspirationSection />
        </div>
      </div>

      <EventModal
        open={eventModal.open}
        onClose={() => setEventModal({ open: false, editing: null, scope: "all" })}
        calendars={calendars}
        editing={eventModal.editing}
        scope={eventModal.scope}
        day={selected}
      />
      <TaskModal
        open={taskModal.open}
        editing={taskModal.editing}
        onClose={() => setTaskModal({ open: false, editing: null })}
      />
    </>
  );
}
